"use client";

import { c as _c } from "react/compiler-runtime";
import { useCarePlanContext } from "../care-plan";
import { DEFAULT_TAG_COLOR } from "../constants";
import { CycleBadge } from "../cycle-badge";
import { formatMinutes } from "../step-detail-helpers";
import { StepDetailLayout } from "../step-detail-layout";
import { ProviderNotesField } from "../step-detail-shared";
import { CarePlanLogField, isPatientStep } from "../types";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { RiAddLine, RiCloseLine, RiEditLine } from "react-icons/ri";
import { TodaysAppointments } from "@/components/right-sidebar/patient/todays-appointments";
import Badge from "@/components/ui/badge";
import { Dialog, DialogBody, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { formatDate, formatStaffName, formatToHumanReadable } from "@/lib/format-data";
import { clickableText, cx } from "@/lib/tailwind-utils";
import { type ItemDiffStatus, computeCollectionDiff, computeMeasurementDiff } from "./compute-item-diff";
import { useDiffContext } from "./diff-context";
import { type DiffStatus, type FieldChange } from "./types";
import { carePlanTagQueryOptions } from "@/api/provider/settings/care-plan-tag-query";

// ============================================================================
// DiffStepDetail — explicit variant of AppointmentStepDetail for audit trail
//
// Reuses: ProviderNotesField (from step-detail-shared)
// Reuses: CarePlanContext lookups for name resolution
// Reads:  DiffContext for per-step annotations
// ============================================================================

export function DiffStepDetail() {
  const $ = _c(54);
  const {
    state: t0,
    lookups: t1,
    cycles: t2
  } = useCarePlanContext();
  const {
    selectedStep
  } = t0;
  const {
    services,
    medicationOptions,
    measurementOptions
  } = t1;
  const {
    cycles: allCycles,
    cycleColorMap
  } = t2;
  const {
    annotations
  } = useDiffContext();
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  if (!selectedStep) {
    return null;
  }
  let t3;
  if ($[0] !== selectedStep) {
    t3 = isPatientStep(selectedStep) ? selectedStep : null;
    $[0] = selectedStep;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const patientStep = t3;
  let T0;
  let eventIdChange;
  let fieldChanges;
  let t10;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let tagChange;
  if ($[2] !== annotations || $[3] !== measurementOptions || $[4] !== medicationOptions || $[5] !== patientStep || $[6] !== selectedStep.id || $[7] !== selectedStep.measurements || $[8] !== selectedStep.medicationIds || $[9] !== selectedStep.serviceIds || $[10] !== services) {
    const annotation = annotations.get(selectedStep.id);
    fieldChanges = annotation?.fieldChanges ?? [];
    const serviceChange = fieldChanges.find(_temp);
    const medicationChange = fieldChanges.find(_temp2);
    const measurementChange = fieldChanges.find(_temp3);
    const descriptionChange = fieldChanges.find(_temp4);
    const statusChange = fieldChanges.find(_temp5);
    eventIdChange = fieldChanges.find(_temp6);
    tagChange = fieldChanges.find(_temp7);
    let t11;
    if ($[22] !== services) {
      t11 = new Map(services.map(_temp8));
      $[22] = services;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    const serviceNameById = t11;
    let t12;
    if ($[24] !== medicationOptions) {
      t12 = new Map(medicationOptions.map(_temp9));
      $[24] = medicationOptions;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    const medNameById = t12;
    let t13;
    if ($[26] !== measurementOptions) {
      t13 = new Map(measurementOptions.map(_temp10));
      $[26] = measurementOptions;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    const measNameById = t13;
    const hasServices = selectedStep.serviceIds.length > 0 || !!serviceChange;
    const hasMedications = selectedStep.medicationIds.length > 0 || !!medicationChange;
    const hasMeasurements = selectedStep.measurements.length > 0 || !!measurementChange;
    T0 = StepDetailLayout;
    t4 = "bg-background/70 ml-auto backdrop-blur-sm max-w-xs w-full max-h-[calc(100%-5rem)] overflow-y-auto pointer-events-auto shadow-md rounded-md p-4 border";
    t5 = annotation && annotation.status !== "unchanged" && <StatusBanner status={annotation.status} />;
    t6 = hasServices && <div className="space-y-1"><Label>Services</Label>{serviceChange ? <DiffBadgeList change={serviceChange} resolveId={id => serviceNameById.get(id)} /> : <div className="flex flex-wrap gap-1.5">{selectedStep.serviceIds.map(id_0 => {
          const name = serviceNameById.get(id_0);
          return name ? <Badge key={id_0}>{name}</Badge> : null;
        })}</div>}</div>;
    t7 = hasMedications && <div className="space-y-1"><Label>Medications</Label>{medicationChange ? <DiffBadgeList change={medicationChange} resolveId={id_1 => medNameById.get(id_1)} /> : <div className="flex flex-wrap gap-1.5">{selectedStep.medicationIds.map(id_2 => {
          const name_0 = medNameById.get(id_2);
          return name_0 ? <Badge key={id_2} variant="neutral">{name_0}</Badge> : null;
        })}</div>}</div>;
    t8 = hasMeasurements && <div className="space-y-1"><Label>Measurements</Label>{measurementChange ? <MeasurementDiffBadgeList change={measurementChange} resolveId={id_3 => measNameById.get(id_3)} /> : <div className="flex flex-wrap gap-1.5">{selectedStep.measurements.map(m => {
          const name_1 = measNameById.get(m.measurementField);
          return name_1 ? <Badge key={m.measurementField} variant="neutral">{name_1}</Badge> : null;
        })}</div>}</div>;
    t9 = descriptionChange ? <div className="space-y-1"><Label>Provider Notes</Label><DiffTextField change={descriptionChange} /></div> : <ProviderNotesField />;
    t10 = statusChange ? <div className="space-y-1"><Label>Status</Label><div className="flex items-center gap-1.5">{statusChange.oldValue && <Badge variant="neutral" className="line-through opacity-60">{formatToHumanReadable(statusChange.oldValue)}</Badge>}{statusChange.oldValue && statusChange.newValue && <span className="text-caption text-foreground-muted">→</span>}{statusChange.newValue && <Badge variant="success">{formatToHumanReadable(statusChange.newValue)}</Badge>}</div></div> : patientStep ? <div className="space-y-1"><Label>Status</Label><Badge>{formatToHumanReadable(patientStep.status)}</Badge></div> : null;
    $[2] = annotations;
    $[3] = measurementOptions;
    $[4] = medicationOptions;
    $[5] = patientStep;
    $[6] = selectedStep.id;
    $[7] = selectedStep.measurements;
    $[8] = selectedStep.medicationIds;
    $[9] = selectedStep.serviceIds;
    $[10] = services;
    $[11] = T0;
    $[12] = eventIdChange;
    $[13] = fieldChanges;
    $[14] = t10;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = tagChange;
  } else {
    T0 = $[11];
    eventIdChange = $[12];
    fieldChanges = $[13];
    t10 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
    tagChange = $[21];
  }
  const t11 = patientStep?.eventId;
  let t12;
  if ($[28] !== eventDialogOpen || $[29] !== eventIdChange || $[30] !== t11) {
    t12 = <EventIdField eventId={t11} change={eventIdChange} dialogOpen={eventDialogOpen} onOpenChange={setEventDialogOpen} />;
    $[28] = eventDialogOpen;
    $[29] = eventIdChange;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== tagChange) {
    t13 = tagChange && <div className="space-y-1"><Label>Tags</Label><TagDiffList change={tagChange} /></div>;
    $[32] = tagChange;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== allCycles || $[35] !== cycleColorMap || $[36] !== selectedStep.cycleId || $[37] !== selectedStep.cyclePass) {
    t14 = selectedStep.cycleId && selectedStep.cyclePass != null && <div className="space-y-1"><Label>Cycle</Label><div className="flex flex-wrap gap-1.5"><CycleBadge cyclePass={selectedStep.cyclePass} colorIndex={cycleColorMap.get(selectedStep.cycleId) ?? 0} cycleName={allCycles.find(c_6 => c_6.id === selectedStep.cycleId)?.name} /></div></div>;
    $[34] = allCycles;
    $[35] = cycleColorMap;
    $[36] = selectedStep.cycleId;
    $[37] = selectedStep.cyclePass;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== fieldChanges) {
    t15 = <OtherFieldChanges fieldChanges={fieldChanges} />;
    $[39] = fieldChanges;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== T0 || $[42] !== t10 || $[43] !== t12 || $[44] !== t13 || $[45] !== t14 || $[46] !== t15 || $[47] !== t4 || $[48] !== t5 || $[49] !== t6 || $[50] !== t7 || $[51] !== t8 || $[52] !== t9) {
    t16 = <T0 className={t4}>{t5}{t6}{t7}{t8}{t9}{t10}{t12}{t13}{t14}{t15}</T0>;
    $[41] = T0;
    $[42] = t10;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
    $[46] = t15;
    $[47] = t4;
    $[48] = t5;
    $[49] = t6;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  return t16;
}

// ============================================================================
// DiffBadgeList — renders collection items as individual diff-aware badges
// ============================================================================
function _temp10(o_0) {
  return [o_0.value, o_0.label];
}
function _temp9(o) {
  return [o.value, o.label];
}
function _temp8(s) {
  return [s.id, s.name];
}
function _temp7(c_5) {
  return c_5.fieldName === CarePlanLogField.BRANCH_TAG_IDS;
}
function _temp6(c_4) {
  return c_4.fieldName === CarePlanLogField.EVENT_ID;
}
function _temp5(c_3) {
  return c_3.fieldName === CarePlanLogField.STATUS || c_3.fieldName === "STATUS";
}
function _temp4(c_2) {
  return c_2.fieldName === CarePlanLogField.STEP_DESCRIPTION;
}
function _temp3(c_1) {
  return c_1.fieldName === CarePlanLogField.MEASUREMENTS;
}
function _temp2(c_0) {
  return c_0.fieldName === CarePlanLogField.MEDICATION_IDS;
}
function _temp(c) {
  return c.fieldName === CarePlanLogField.SERVICE_IDS;
}
const ITEM_BADGE_CONFIG: Record<ItemDiffStatus, {
  variant: "default" | "success" | "error";
  className: string;
}> = {
  unchanged: {
    variant: "default",
    className: ""
  },
  added: {
    variant: "success",
    className: ""
  },
  removed: {
    variant: "error",
    className: "line-through opacity-60"
  }
};
function DiffBadgeList(t0) {
  const $ = _c(11);
  const {
    change,
    resolveId
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== change || $[1] !== resolveId) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const items = computeCollectionDiff(change);
      if (items.length === 0) {
        let t4;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <span className="text-body text-foreground-muted">None</span>;
          $[5] = t4;
        } else {
          t4 = $[5];
        }
        t3 = t4;
        break bb0;
      }
      t1 = "flex flex-wrap gap-1.5";
      let t4;
      if ($[6] !== resolveId) {
        t4 = item => {
          const config = ITEM_BADGE_CONFIG[item.status];
          const label = resolveId(item.id) ?? item.displayName;
          return <Badge key={`${item.id}-${item.status}`} variant={config.variant} className={config.className}>{label}</Badge>;
        };
        $[6] = resolveId;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      t2 = items.map(t4);
    }
    $[0] = change;
    $[1] = resolveId;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2) {
    t4 = <div className={t1}>{t2}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}

/**
 * MeasurementDiffBadgeList — handles measurement-specific JSON object format.
 * Measurements are serialized as `[{"measurement_field": "...", ...}]`
 * instead of simple string arrays, so we use computeMeasurementDiff.
 */
function MeasurementDiffBadgeList(t0) {
  const $ = _c(11);
  const {
    change,
    resolveId
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== change || $[1] !== resolveId) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const items = computeMeasurementDiff(change);
      if (items.length === 0) {
        let t4;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <span className="text-body text-foreground-muted">None</span>;
          $[5] = t4;
        } else {
          t4 = $[5];
        }
        t3 = t4;
        break bb0;
      }
      t1 = "flex flex-wrap gap-1.5";
      let t4;
      if ($[6] !== resolveId) {
        t4 = item => {
          const config = ITEM_BADGE_CONFIG[item.status];
          const label = resolveId(item.id) ?? item.displayName;
          return <Badge key={`${item.id}-${item.status}`} variant={config.variant} className={config.className}>{label}</Badge>;
        };
        $[6] = resolveId;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      t2 = items.map(t4);
    }
    $[0] = change;
    $[1] = resolveId;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2) {
    t4 = <div className={t1}>{t2}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}

// ============================================================================
// TagDiffList — renders tag diffs with colored tag pills
// ============================================================================

function TagDiffList(t0) {
  const $ = _c(14);
  const {
    change
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = carePlanTagQueryOptions.list();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: allTags
  } = useQuery(t1);
  let t2;
  let t3;
  let t4;
  if ($[1] !== allTags || $[2] !== change) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const items = computeCollectionDiff(change);
      let t5;
      if ($[6] !== allTags) {
        t5 = new Map((allTags ?? []).map(_temp11));
        $[6] = allTags;
        $[7] = t5;
      } else {
        t5 = $[7];
      }
      const tagById = t5;
      if (items.length === 0) {
        let t6;
        if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <span className="text-body text-foreground-muted">None</span>;
          $[8] = t6;
        } else {
          t6 = $[8];
        }
        t4 = t6;
        break bb0;
      }
      t2 = "flex flex-wrap gap-1.5";
      let t6;
      if ($[9] !== tagById) {
        t6 = item => {
          const tag = tagById.get(item.id);
          const color = tag?.color ?? DEFAULT_TAG_COLOR;
          const label = tag?.name ?? item.displayName;
          const isRemoved = item.status === "removed";
          return <span key={`${item.id}-${item.status}`} className={cx("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-tiny font-medium", isRemoved && "line-through opacity-60")} style={{
            backgroundColor: `${color}20`,
            color
          }}><span className="h-1.5 w-1.5 rounded-full" style={{
              backgroundColor: color
            }} /><span><span>{item.status === "added" && "+ "}</span>{label}</span></span>;
        };
        $[9] = tagById;
        $[10] = t6;
      } else {
        t6 = $[10];
      }
      t3 = items.map(t6);
    }
    $[1] = allTags;
    $[2] = change;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3) {
    t5 = <div className={t2}>{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}

// ============================================================================
// DiffTextField — shows old→new for scalar text fields
// ============================================================================
function _temp11(t) {
  return [t.id, t];
}
function DiffTextField(t0) {
  const $ = _c(7);
  const {
    change
  } = t0;
  let t1;
  if ($[0] !== change.oldValue) {
    t1 = change.oldValue && <p className="text-foreground-secondary line-through">{change.oldValue}</p>;
    $[0] = change.oldValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== change.newValue) {
    t2 = change.newValue && <p className="text-foreground">{change.newValue}</p>;
    $[2] = change.newValue;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="space-y-1 text-body">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

// ============================================================================
// Status banner
// ============================================================================

const BANNER_CONFIG: Record<Exclude<DiffStatus, "unchanged">, {
  label: string;
  icon: typeof RiAddLine;
  variant: "success" | "error" | "warning";
  bg: string;
}> = {
  added: {
    label: "Added in this revision",
    icon: RiAddLine,
    variant: "success",
    bg: "bg-status-success-bg"
  },
  removed: {
    label: "Removed in this revision",
    icon: RiCloseLine,
    variant: "error",
    bg: "bg-status-error-bg"
  },
  modified: {
    label: "Modified in this revision",
    icon: RiEditLine,
    variant: "warning",
    bg: "bg-status-warning-bg"
  }
};
function StatusBanner(t0) {
  const $ = _c(9);
  const {
    status
  } = t0;
  const config = BANNER_CONFIG[status];
  let t1;
  if ($[0] !== config.bg) {
    t1 = cx("rounded-md px-2.5 py-1.5", config.bg);
    $[0] = config.bg;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== config.icon || $[3] !== config.label || $[4] !== config.variant) {
    t2 = <Badge variant={config.variant} size="xs" icon={config.icon}>{config.label}</Badge>;
    $[2] = config.icon;
    $[3] = config.label;
    $[4] = config.variant;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

// ============================================================================
// EventIdField — shows event ID with clickable "View Event" that opens dialog
// ============================================================================

function EventIdField(t0) {
  const $ = _c(11);
  const {
    eventId,
    change,
    dialogOpen,
    onOpenChange
  } = t0;
  const currentEventId = change?.newValue ?? eventId;
  if (!currentEventId && !change) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Label>Event</Label>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== change || $[2] !== currentEventId) {
    t2 = change?.oldValue && <><span className="text-foreground-secondary line-through">{change.oldValue}</span>{currentEventId && <span className="text-foreground-muted">→</span>}</>;
    $[1] = change;
    $[2] = currentEventId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== currentEventId || $[5] !== dialogOpen || $[6] !== onOpenChange) {
    t3 = currentEventId && <><span className={clickableText} onClick={() => onOpenChange(true)}>View Event</span><Dialog open={dialogOpen} onOpenChange={onOpenChange}><DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Appointment Details</DialogTitle></DialogHeader><DialogBody><TodaysAppointments eventId={currentEventId} /></DialogBody><DialogFooter><DialogClose>Close</DialogClose></DialogFooter></DialogContent></Dialog></>;
    $[4] = currentEventId;
    $[5] = dialogOpen;
    $[6] = onOpenChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <div className="space-y-1">{t1}<div className="flex flex-wrap items-center gap-1.5 text-caption">{t2}{t3}</div></div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}

// ============================================================================
// Other field changes — catch-all for scalar fields not rendered above
// ============================================================================

const HANDLED_FIELDS = new Set([CarePlanLogField.SERVICE_IDS, CarePlanLogField.MEDICATION_IDS, CarePlanLogField.MEASUREMENTS, CarePlanLogField.STEP_DESCRIPTION, CarePlanLogField.STATUS, CarePlanLogField.EVENT_ID, CarePlanLogField.STEP_NUMBER, CarePlanLogField.STEP_TYPE,
// These are structural — not meaningful to show
CarePlanLogField.PARENT_STEP_ID, CarePlanLogField.SOURCE_SERVICE_ID, CarePlanLogField.BRANCH_ID, CarePlanLogField.BRANCH_SELECTED, CarePlanLogField.BRANCH_NAME,
// Legacy field_name before DB migration / backend rename
"BRANCH_DESCRIPTION" as CarePlanLogField, CarePlanLogField.BRANCH_POSITION, CarePlanLogField.BRANCH_TAG_IDS, CarePlanLogField.CYCLE_PASS_COUNT, CarePlanLogField.CYCLE_GAP_MINUTES]);
function OtherFieldChanges(t0) {
  const $ = _c(8);
  const {
    fieldChanges
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== fieldChanges) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const others = fieldChanges.filter(_temp12);
      if (others.length === 0) {
        t3 = null;
        break bb0;
      }
      const resolveValue = _temp13;
      t1 = "space-y-1.5 border-t border-border pt-2";
      let t4;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = (change, i) => <div key={`${change.fieldName}-${i}`} className="space-y-0.5"><Label className="text-caption">{formatToHumanReadable(change.fieldName.replace(/_IDS?$/, "").replace(/^STEP_/, ""))}</Label><div className="flex flex-wrap items-center gap-1.5 text-caption">{change.oldValue && <span className="text-foreground-secondary line-through">{resolveValue(change.fieldName, change.oldValue)}</span>}{change.oldValue && change.newValue && <span className="text-foreground-muted">→</span>}{change.newValue && <span className="text-foreground">{resolveValue(change.fieldName, change.newValue)}</span>}</div></div>;
        $[4] = t4;
      } else {
        t4 = $[4];
      }
      t2 = others.map(t4);
    }
    $[0] = fieldChanges;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2) {
    t4 = <div className={t1}>{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function _temp13(fieldName, value) {
  if (fieldName === CarePlanLogField.TASK_ASSIGNEE_ID) {
    return formatStaffName(value);
  }
  if (fieldName === CarePlanLogField.MINUTES_AFTER_PREVIOUS || fieldName === CarePlanLogField.TASK_DUE_DATE) {
    const mins = parseInt(value, 10);
    return isNaN(mins) ? value : formatMinutes(mins);
  }
  if (fieldName === CarePlanLogField.SCHEDULED_DATE) {
    return formatDate(value) || value;
  }
  return formatToHumanReadable(value);
}
function _temp12(c) {
  return !HANDLED_FIELDS.has(c.fieldName as CarePlanLogField);
}